import { useEffect, useState } from "react";
import {
  NavLink,
  Outlet,
  useLocation,
  useNavigate,
  useParams,
} from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { App as AntApp, Dropdown, Input, Popover } from "antd";
import {
  Bot,
  ChevronDown,
  Library,
  LogOut,
  MessageSquarePlus,
  MoreHorizontal,
  PanelLeft,
  PenLine,
  Quote,
  Search,
  Settings,
  Target,
  Inbox,
  CheckSquare,
  FolderKanban,
  CalendarDays,
  Lightbulb,
  Users,
  ClipboardCheck,
  LayoutDashboard,
  House,
  Blocks,
} from "lucide-react";
import { deleteSession, listSessions, renameSession } from "../../api/chat";
import { deleteThink, listThinks } from "../../api/think";
import { deleteResearch, listResearch } from "../../api/research";
import { logout } from "../../api/auth";
import { getStoredUser } from "../../api/session";
import { IconButton, Segmented } from "../../ui";
import { BrandMark } from "../../ui/BrandMark";
import { SearchPalette } from "../search/SearchPalette";
import { DocSheet } from "../kb/DocSheet";
import { useAppearanceSettings } from "../../theme/ThemeProvider";
import { groupSessions } from "./sessionGroups";
import "./shell.css";

const SIDEBAR_KEY = "lingzhi-web.sidebar-collapsed";

export function Shell() {
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(SIDEBAR_KEY) === "1";
    } catch {
      return false;
    }
  });
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const location = useLocation();
  // 侧栏常驻的几页（和从「最近」打开的对话）之外，都在「更多」里
  const inMore =
    location.pathname !== "/" &&
    !["/dashboard", "/inbox", "/tasks", "/projects", "/calendar", "/chat/", "/home"].some((p) => location.pathname.startsWith(p));
  // 知识库三栏要地方，写作页只留一张纸：进来时侧栏自动收成图标，按侧栏按钮可以临时展开（换页就收回去）
  const autoRail =
    /^\/kb\/[^/]+(\/d\/[^/]+)?\/?$/.test(location.pathname) ||
    /^\/write\/(?!entry\/)[^/]+\/?$/.test(location.pathname);
  const [peek, setPeek] = useState(false);

  useEffect(() => {
    setMobileOpen(false);
    setPeek(false);
  }, [location.pathname]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const toggle = () => {
    if (matchMedia("(max-width: 900px)").matches)
      return setMobileOpen((v) => !v);
    if (autoRail && !collapsed) return setPeek((v) => !v);
    setCollapsed((v) => {
      try {
        localStorage.setItem(SIDEBAR_KEY, v ? "0" : "1");
      } catch {
        // 记不住也没关系
      }
      return !v;
    });
  };

  return (
    <div
      className="shell"
      data-collapsed={collapsed || (autoRail && !peek)}
      data-mobile-open={mobileOpen}
    >
      <aside className="side" aria-label="导航">
        <div className="brand">
          <BrandMark />
          <b>灵智原生</b>
          <IconButton label="搜索（⌘K）" onClick={() => setSearchOpen(true)}>
            <Search />
          </IconButton>
          <IconButton label="收起侧栏" onClick={toggle}>
            <PanelLeft />
          </IconButton>
        </div>
        <div className="platform-nav">
          {[
            { to: "/dashboard", label: "仪表盘", icon: <LayoutDashboard /> },
            { to: "/", label: "首页", icon: <House /> },
            { to: "/direction", label: "方向与优先级", icon: <Target /> },
            { to: "/brain", label: "中枢", icon: <Bot /> },
            { to: "/inbox", label: "收件箱", icon: <Inbox /> },
            { to: "/tasks", label: "看板", icon: <CheckSquare /> },
            { to: "/projects", label: "进度表", icon: <FolderKanban /> },
            { to: "/calendar", label: "月历", icon: <CalendarDays /> },
          ].map((n) => (
            <NavLink key={n.to} to={n.to} end={n.to === "/"} className="nav">
              {n.icon}
              {n.label}
            </NavLink>
          ))}
          {/* 当前页在「更多」里时自动展开，看得到自己在哪 */}
          <details className="platform-more" open={inMore || undefined} key={inMore ? "in" : "out"}>
            <summary className="nav">
              <MoreHorizontal />
              更多
            </summary>
            {/* 少即是多：侧栏只留每天用的六页，其余都在「更多」里 */}
            <NavLink to="/okr" className="nav"><Target />OKR</NavLink>
            <NavLink to="/customers" className="nav"><Users />客户</NavLink>
            <NavLink to="/ask" className="nav"><MessageSquarePlus />问答</NavLink>
            <NavLink to="/kb" className="nav"><Library />资料库</NavLink>
            <NavLink to="/inspiration" className="nav"><Lightbulb />灵感墙</NavLink>
            <NavLink to="/approvals" className="nav"><ClipboardCheck />审批中心</NavLink>
            <NavLink to="/project-agents" className="nav"><Bot />项目管家</NavLink>
            <NavLink to="/ai-actions" className="nav"><Bot />AI 动作账本</NavLink>
            <NavLink to="/settings/platform/company-rules" className="nav"><Library />公司规矩</NavLink>
            <NavLink to="/personal-home" className="nav"><House />我的主页</NavLink>
            <NavLink to="/apps" className="nav"><Blocks />应用中心</NavLink>
            <NavLink to="/think" className="nav">
              <Bot />
              思考
            </NavLink>
            <NavLink to="/research" className="nav">
              <Search />
              研究
            </NavLink>
            <NavLink to="/insights" className="nav">
              <Quote />
              观点
            </NavLink>
            <NavLink to="/write" className="nav">
              <PenLine />
              写作
            </NavLink>
            <NavLink to="/agents" className="nav">
              <Bot />
              智能体
            </NavLink>
            <NavLink to="/decisions" className="nav">
              <ClipboardCheck />
              决策日志
            </NavLink>
            <NavLink to="/settings/platform" className="nav">
              <Settings />
              中台设置
            </NavLink>
            <NavLink to="/settings" className="nav">
              <Settings />
              账号设置
            </NavLink>
          </details>
        </div>
        <h2>最近</h2>
        <SessionList />
        <UserMenu />
      </aside>
      {mobileOpen && (
        <div
          className="shell-scrim"
          onClick={() => setMobileOpen(false)}
          aria-hidden="true"
        />
      )}
      <main className="shell-main">
        <div className="shell-open">
          <IconButton label="展开侧栏" onClick={toggle}>
            <PanelLeft />
          </IconButton>
        </div>
        <Outlet />
      </main>
      <nav className="platform-bottom" aria-label="手机主要导航">
        {[
          { to: "/dashboard", label: "仪表盘", icon: <LayoutDashboard /> },
          { to: "/", label: "首页", icon: <House /> },
          { to: "/tasks", label: "看板", icon: <CheckSquare /> },
          { to: "/calendar", label: "月历", icon: <CalendarDays /> },
        ].map((n) => (
          <NavLink key={n.to} to={n.to} end={n.to === "/"}>
            {n.icon}
            <span>{n.label}</span>
          </NavLink>
        ))}
        <button aria-label="更多导航" onClick={() => setMobileOpen(true)}><MoreHorizontal/><span>更多</span></button>
      </nav>
      <SearchPalette open={searchOpen} onClose={() => setSearchOpen(false)} />
      <DocSheet />
    </div>
  );
}

/** 「最近」里的一行：普通对话、一次思考讨论（带「思考」小标签）或一次深入研究（带「研究」）。 */
interface Recent {
  kind: "chat" | "think" | "research";
  id: string;
  title: string;
  is_pinned?: boolean;
  created_at?: string;
  updated_at?: string;
}

function SessionList() {
  const { sessionId, thinkId, researchId } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { modal, message } = AntApp.useApp();
  const sessions = useQuery({
    queryKey: ["sessions"],
    queryFn: () => listSessions(1, 30),
  });
  // 思考室连不上时，「最近」照样列出对话
  const thinks = useQuery({ queryKey: ["thinks"], queryFn: listThinks });
  const researches = useQuery({
    queryKey: ["research"],
    queryFn: listResearch,
  });
  const currentId = (r: Recent) =>
    r.kind === "think"
      ? thinkId
      : r.kind === "research"
        ? researchId
        : sessionId;
  const remove = useMutation({
    mutationFn: (r: Recent) =>
      r.kind === "think"
        ? deleteThink(r.id)
        : r.kind === "research"
          ? deleteResearch(r.id)
          : deleteSession(r.id),
    onSuccess: (_d, r) => {
      qc.invalidateQueries({
        queryKey: [
          r.kind === "think"
            ? "thinks"
            : r.kind === "research"
              ? "research"
              : "sessions",
        ],
      });
      if (r.id === currentId(r)) navigate("/ask");
    },
    onError: (e) => void message.error(e.message),
  });
  const rename = useMutation({
    mutationFn: ({ id, title }: { id: string; title: string }) =>
      renameSession(id, title),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["sessions"] }),
    onError: (e) => void message.error(e.message),
  });

  if (sessions.isPending) return <div className="hist" aria-busy="true" />;
  if (sessions.isError) return <p className="hist-empty">没能载入最近的对话</p>;
  const time = (r: Recent) =>
    new Date(r.updated_at || r.created_at || 0).getTime() || 0;
  const items: Recent[] = [
    ...(sessions.data.data ?? []).map((c) => ({
      kind: "chat" as const,
      id: c.id,
      title: c.title,
      is_pinned: c.is_pinned,
      created_at: c.created_at,
      updated_at: c.updated_at,
    })),
    ...(thinks.data ?? []).map((t) => ({
      kind: "think" as const,
      id: t.id,
      title: t.topic,
      created_at: t.createdAt,
      updated_at: t.updatedAt,
    })),
    ...(researches.data ?? []).map((t) => ({
      kind: "research" as const,
      id: t.id,
      title: t.question,
      created_at: t.createdAt,
      updated_at: t.updatedAt,
    })),
  ].sort((a, b) => time(b) - time(a));
  const groups = groupSessions(items);
  if (!groups.length) return <p className="hist-empty">还没有对话</p>;

  const askRename = (id: string, title: string) => {
    let value = title;
    modal.confirm({
      title: "重命名对话",
      icon: null,
      content: (
        <Input
          defaultValue={title}
          autoFocus
          onChange={(e) => (value = e.target.value)}
          maxLength={80}
        />
      ),
      okText: "保存",
      cancelText: "取消",
      onOk: () =>
        value.trim() && value.trim() !== title
          ? rename.mutateAsync({ id, title: value.trim() })
          : undefined,
    });
  };
  const askDelete = (r: Recent) =>
    modal.confirm({
      title:
        r.kind === "think"
          ? "删除这次思考讨论？"
          : r.kind === "research"
            ? "删除这次研究？"
            : "删除这个对话？",
      content:
        r.kind === "think"
          ? `「${r.title}」的讨论过程会删掉，不能恢复；已经存进知识库的判决书和分歧地图还在。`
          : r.kind === "research"
            ? `「${r.title}」的研究报告会删掉，不能恢复。`
            : `「${r.title || "未命名对话"}」会从灵智原生里删掉，不能恢复。`,
      okText: "删除",
      okButtonProps: { danger: true },
      cancelText: "取消",
      onOk: () => remove.mutateAsync(r),
    });

  return (
    <nav className="hist" aria-label="最近的对话">
      {groups.map((g) => (
        <div key={g.label} role="group" aria-label={g.label}>
          <div className="hist-group">{g.label}</div>
          {g.items.map((s) => (
            <div
              key={`${s.kind}-${s.id}`}
              className="hist-row"
              data-current={s.id === currentId(s)}
            >
              <NavLink
                to={
                  s.kind === "think"
                    ? `/think/${s.id}`
                    : s.kind === "research"
                      ? `/research/${s.id}`
                      : `/chat/${s.id}`
                }
                title={s.title}
              >
                {s.title || "未命名对话"}
              </NavLink>
              {s.kind === "think" && <span className="tag-think">思考</span>}
              {s.kind === "research" && (
                <span className="tag-think tag-research">研究</span>
              )}
              <Dropdown
                trigger={["click"]}
                menu={{
                  items:
                    s.kind !== "chat"
                      ? [{ key: "delete", label: "删除", danger: true }]
                      : [
                          { key: "rename", label: "重命名" },
                          { key: "delete", label: "删除", danger: true },
                        ],
                  onClick: ({ key }) =>
                    key === "rename" ? askRename(s.id, s.title) : askDelete(s),
                }}
              >
                <IconButton label={`「${s.title || "未命名对话"}」的更多操作`}>
                  <MoreHorizontal />
                </IconButton>
              </Dropdown>
            </div>
          ))}
        </div>
      ))}
    </nav>
  );
}

function AppearancePanel() {
  const { appearance, setAppearance, isDark } = useAppearanceSettings();
  return (
    <>
      <div>
        <h3>深浅</h3>
        <Segmented
          label="深浅"
          value={appearance.theme}
          onChange={(theme) => setAppearance({ theme })}
          items={[
            { value: "system", label: "跟随系统" },
            { value: "light", label: "浅色" },
            { value: "dark", label: "深色" },
          ]}
        />
      </div>
      <div
        aria-disabled={isDark}
        style={isDark ? { opacity: 0.5, pointerEvents: "none" } : undefined}
      >
        <h3>配色{isDark ? "（只在浅色下）" : ""}</h3>
        <Segmented
          label="配色"
          value={appearance.tone}
          onChange={(tone) => setAppearance({ tone })}
          items={[
            { value: "ink", label: "暖白" },
            { value: "paper", label: "纸面" },
          ]}
        />
      </div>
      <div>
        <h3>标题字体</h3>
        <Segmented
          label="标题字体"
          value={appearance.title}
          onChange={(title) => setAppearance({ title })}
          items={[
            { value: "serif", label: "宋体" },
            { value: "sans", label: "黑体" },
          ]}
        />
      </div>
    </>
  );
}

function UserMenu() {
  const user = getStoredUser();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const name = user?.username || user?.email || "我";
  const doLogout = async () => {
    setOpen(false);
    await logout().catch(() => undefined);
    qc.clear();
    navigate("/login", { replace: true });
  };
  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      trigger="click"
      placement="topLeft"
      arrow={false}
      content={
        <div className="me-panel">
          <AppearancePanel />
          <div className="me-links">
            <NavLink
              to="/settings"
              className="nav"
              onClick={() => setOpen(false)}
            >
              <Settings />
              设置
            </NavLink>
            <button type="button" className="nav" onClick={doLogout}>
              <LogOut />
              退出登录
            </button>
          </div>
        </div>
      }
    >
      <button
        type="button"
        className="me"
        aria-expanded={open}
        aria-haspopup="dialog"
      >
        <span className="avatar" aria-hidden="true">
          {name.slice(0, 1).toUpperCase()}
        </span>
        <span className="me-text">
          {name}
          {user?.email && <small>{user.email}</small>}
        </span>
        <ChevronDown className="lucide" width={16} height={16} />
      </button>
    </Popover>
  );
}
